/**
 * Credential Authentication Component
 *
 * Provides secure device-based authentication using platform authenticators.
 * Supports both registration and authentication modes with fallback options.
 */

'use client';

import React, { useState, useEffect } from 'react';
import { Button, Card, Alert, Spin, Typography, Space, Divider } from 'antd';
import {
  KeyOutlined,
  UserAddOutlined,
  LoginOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  LockOutlined
} from '@ant-design/icons';
import { authService } from '@/domains/auth/services/auth-service';

const { Title, Text, Paragraph } = Typography;

interface CredentialAuthProps {
  mode: 'register' | 'authenticate';
  username?: string;
  displayName?: string;
  onSuccess?: (result: any) => void;
  onError?: (error: string) => void;
  onFallback?: () => void;
  className?: string;
}

export const CredentialAuth: React.FC<CredentialAuthProps> = ({
  mode,
  username = '',
  displayName = '',
  onSuccess,
  onError,
  onFallback,
  className
}) => {
  const [isSupported, setIsSupported] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [step, setStep] = useState<'initial' | 'processing' | 'complete'>('initial');

  useEffect(() => {
    // Check if credential authentication is supported
    const checkSupport = async () => {
      try {
        const supported = authService.isCredentialSupported();
        setIsSupported(supported);

        if (!supported) {
          setError('Credential authentication is not supported in this browser. Please use password authentication.');
        }
      } catch (err) {
        setIsSupported(false);
        setError('Unable to check credential authentication support.');
      }
    };

    checkSupport();
  }, []);

  const handleRegistration = async () => {
    if (!username || !displayName) {
      setError('Username and display name are required for registration.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setStep('processing');

    try {
      // Use the integrated AuthService method
      const result = await authService.registerCredential(username, displayName);

      if (result.success) {
        setSuccess('Credential registered successfully!');
        setStep('complete');
        onSuccess?.(result);
      } else {
        throw new Error(result.message || 'Registration failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      setError(errorMessage);
      setStep('initial');
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthentication = async () => {
    if (!username) {
      setError('Username is required for authentication.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setStep('processing');

    try {
      // Use the integrated AuthService method
      const result = await authService.authenticateWithCredential(username);

      if (result.success) {
        setSuccess('Authentication successful!');
        setStep('complete');
        onSuccess?.(result);
      } else {
        throw new Error(result.error || 'Authentication failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
      setError(errorMessage);
      setStep('initial');
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = () => {
    if (mode === 'register') {
      handleRegistration();
    } else {
      handleAuthentication();
    }
  };

  const getActionText = () => {
    if (mode === 'register') {
      return 'Register Credential';
    }
    return 'Sign In with Credential';
  };

  const getDescription = () => {
    if (mode === 'register') {
      return 'Set up secure device-based authentication using your fingerprint, face ID, or security key.';
    }
    return 'Sign in securely using your registered device credential.';
  };

  const getIcon = () => {
    if (mode === 'register') {
      return <UserAddOutlined />;
    }
    return <LoginOutlined />;
  };

  if (isSupported === null) {
    return (
      <Card className={className}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: '16px' }}>
            Checking credential authentication support...
          </Text>
        </div>
      </Card>
    );
  }

  if (isSupported === false) {
    return (
      <Card className={className}>
        <Alert
          title="Credential Authentication Not Supported"
          description="Your browser or device doesn't support credential authentication. Please use password authentication instead."
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          action={
            <Button size="small" type="link" onClick={onFallback}>
              Use Password Authentication
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <Card className={className}>
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {getIcon()}
            </div>
            <Title level={3}>{getActionText()}</Title>
            <Paragraph type="secondary">{getDescription()}</Paragraph>
          </div>

          {/* Error Display */}
          {error && (
            <Alert
              title="Authentication Error"
              description={error}
              type="error"
              showIcon
              closable={{ onClose: () => setError(null) }}
            />
          )}

          {/* Success Display */}
          {success && (
            <Alert
              title="Success"
              description={success}
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
            />
          )}

          {/* Action Button */}
          {step === 'initial' && (
            <Button
              type="primary"
              size="large"
              icon={<KeyOutlined />}
              onClick={handleAction}
              loading={isLoading}
              disabled={isLoading}
              style={{ minWidth: '200px' }}
            >
              {getActionText()}
            </Button>
          )}

          {/* Processing State */}
          {step === 'processing' && (
            <div>
              <Spin size="large" />
              <Text style={{ display: 'block', marginTop: '16px' }}>
                {mode === 'register' ? 'Setting up credential...' : 'Authenticating...'}
              </Text>
              <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
                Please follow your device's authentication prompt
              </Text>
            </div>
          )}

          {/* Fallback Option */}
          {step === 'initial' && onFallback && (
            <>
              <Divider>or</Divider>
              <Button
                type="default"
                size="large"
                icon={<LockOutlined />}
                onClick={onFallback}
                disabled={isLoading}
              >
                Use Password Authentication
              </Button>
            </>
          )}

          {/* Security Note */}
          <div style={{ marginTop: '20px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <LockOutlined /> Your biometric data never leaves your device and is not stored on our servers.
            </Text>
          </div>
        </Space>
      </div>
    </Card>
  );
};

export default CredentialAuth;
